import { createContext, useContext } from 'react';

import type { AccordionLayout, AccordionSurface } from './types';

/**
 * The two container-level choices, passed from the root to each item.
 *
 * ── Why this needs context at all, when everything else here is a selector ────────────────────
 *
 * The rest of this component's container-level state travels as `data-*` attributes on the root and
 * is read by `group-data` selectors, which is the cheaper mechanism and the one the recipe layer
 * prefers: it keeps a visual decision out of React entirely. `iconPlacement` still works that way.
 *
 * `layout` cannot, because it changes the item's STRUCTURE and not only its values. The stacked
 * arrangement is an eyebrow row carrying the leading slot and the indicator, with the title block
 * on a second row beneath; the inline arrangement is one row with the indicator, the leading slot,
 * the title block and the trailing slot as siblings. Those are different element trees, and no CSS
 * selector re-parents anything. Rendering both and hiding one would double the DOM for every row
 * and leave a hidden copy of the title for a find-in-page to hit.
 *
 * `surface` could have been a selector, and is carried here anyway. It picks a different CLASS LIST
 * rather than a different value — the insets and the radius change together, and `surface="none"`
 * zeroes both — and threading one of the pair through context while the other went through the DOM
 * would leave a reader looking in two places to answer one question.
 *
 * Both are also mirrored onto the root as `data-layout` and `data-surface`. The attributes are what
 * a test or a parent stylesheet should read; this context is an implementation detail of how the
 * item picks its variants, and the two always carry the same value.
 *
 * ── The default ───────────────────────────────────────────────────────────────────────────────
 *
 * The defaults here match the root's own prop defaults, so an `AccordionItem` rendered outside an
 * `Accordion` — which only a test would do — draws the inline on-surface card rather than throwing.
 * That is deliberate: a primitive that crashes when it cannot find its parent makes every test of
 * the child require a wrapper, and there is nothing this component does that genuinely needs the
 * root to exist. Base UI's own parts will complain first if the composition is actually wrong.
 */
export type AccordionLayoutContextValue = {
  layout: AccordionLayout;
  surface: AccordionSurface;
};

export const AccordionLayoutContext = createContext<AccordionLayoutContextValue>({
  layout: 'inline',
  surface: 'on-surface',
});

export const useAccordionLayout = () => useContext(AccordionLayoutContext);
